import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { SYSTEM_CONSOLE_DEFAULT_PATH } from '../../config/routes';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Alert from '@mui/material/Alert';
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';
import CircularProgress from '@mui/material/CircularProgress';
import Link from '@mui/material/Link';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import PersonIcon from '@mui/icons-material/Person';
import LockIcon from '@mui/icons-material/Lock';

const MAX_LOGIN_ATTEMPTS = 3;
const LOCKOUT_TIME = 5 * 60 * 1000; // 5 минут в миллисекундах

function LoginForm() {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();
  const [credentials, setCredentials] = useState({
    username: '',
    password: ''
  });
  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [, setLoginAttempts] = useState(0);
  const [lockoutEnd, setLockoutEnd] = useState(null);
  const [remainingTime, setRemainingTime] = useState(0);
  const submittingRef = useRef(false);

  // Загрузка сохраненного логина
  useEffect(() => {
    const savedLogin = localStorage.getItem('lastLogin');
    if (savedLogin) {
      setCredentials(prev => ({
        ...prev,
        username: savedLogin
      }));
    }
  }, []);

  // Обработка таймера блокировки
  useEffect(() => {
    if (lockoutEnd) {
      const interval = setInterval(() => {
        const remaining = Math.max(0, lockoutEnd - Date.now());
        if (remaining === 0) {
          setLockoutEnd(null);
          setLoginAttempts(0);
          clearInterval(interval);
        }
        setRemainingTime(Math.ceil(remaining / 1000));
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [lockoutEnd]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setCredentials(prev => ({
      ...prev,
      [name]: value
    }));
    // Очищаем ошибку поля при изменении
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!credentials.username.trim()) {
      newErrors.username = 'Введите логин';
    }
    if (!credentials.password) {
      newErrors.password = 'Введите пароль';
    } else if (credentials.password.length < 6) {
      newErrors.password = 'Пароль должен содержать минимум 6 символов';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Проверяем блокировку
    if (lockoutEnd && Date.now() < lockoutEnd) {
        return;
    }

    if (!validateForm()) {
        return;
    }

    if (submittingRef.current) {
      return;
    }
    submittingRef.current = true;

    setLoading(true);
    setError('');

    try {
        const userData = await login(credentials);
        localStorage.setItem('lastLogin', credentials.username);
        if (userData?.must_change_password) {
          navigate('/change-password', { replace: true });
          return;
        }
        const fromPath = location.state?.from?.pathname;
        let redirectTo = fromPath || '/';
        if (userData?.profile_type_code === 'system_console' && !String(redirectTo).startsWith('/admin/system')) {
          redirectTo = SYSTEM_CONSOLE_DEFAULT_PATH;
        }
        navigate(redirectTo, { replace: true });
    } catch (err) {
        setLoginAttempts(prev => {
            const newAttempts = prev + 1;
            if (newAttempts >= MAX_LOGIN_ATTEMPTS) {
                setLockoutEnd(Date.now() + LOCKOUT_TIME);
            }
            return newAttempts;
        });

        // Улучшенная обработка ошибок (backend возвращает detail, не message)
        const detail = err.response?.data?.detail;
        if (err.response) {
            if (err.response.status === 401) {
                setError(typeof detail === 'string' ? detail : 'Неправильное имя пользователя или пароль');
            } else {
                setError(typeof detail === 'string' ? detail : `Ошибка авторизации (код ${err.response.status}). Попробуйте позже`);
            }
        } else {
            setError(err.message || 'Ошибка авторизации. Попробуйте позже');
        }
    } finally {
        submittingRef.current = false;
        setLoading(false);
    }
  };

  const handleTogglePassword = () => {
    setShowPassword(prev => !prev);
  };

  const isLocked = lockoutEnd && Date.now() < lockoutEnd;

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: theme => theme.palette.background.default
      }}
    >
      <Paper
        elevation={3}
        sx={{
          p: 4,
          width: '100%',
          maxWidth: 400,
          textAlign: 'center'
        }}
      >
        <Typography variant="h4" gutterBottom>
          Вход в систему
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} aria-live="polite">
            {error}
          </Alert>
        )}

        {isLocked && (
          <Alert severity="warning" sx={{ mb: 2 }} aria-live="polite">
            Слишком много попыток входа. Попробуйте снова через {Math.ceil(remainingTime / 60)} мин. {remainingTime % 60} сек.
          </Alert>
        )}

        <form onSubmit={handleSubmit}>
          <TextField
            fullWidth
            label="Логин"
            name="username"
            autoComplete="username"
            value={credentials.username}
            onChange={handleChange}
            error={Boolean(errors.username)}
            helperText={errors.username}
            disabled={loading || isLocked}
            margin="normal"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <PersonIcon />
                </InputAdornment>
              ),
            }}
          />

          <TextField
            fullWidth
            type={showPassword ? 'text' : 'password'}
            label="Пароль"
            name="password"
            autoComplete="current-password"
            value={credentials.password}
            onChange={handleChange}
            error={Boolean(errors.password)}
            helperText={errors.password}
            disabled={loading || isLocked}
            margin="normal"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <LockIcon />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={handleTogglePassword}
                    disabled={loading || isLocked}
                    edge="end"
                    aria-label={showPassword ? 'Скрыть пароль' : 'Показать пароль'}
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          <Button
            type="submit"
            variant="contained"
            fullWidth
            disabled={loading || isLocked}
            sx={{ mt: 3, mb: 2 }}
          >
            {loading ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              'Войти'
            )}
          </Button>

          <Link
            component="a"
            variant="body2"
            href="/forgot-password"
            sx={{ textDecoration: 'none' }}
          >
            Забыли пароль?
          </Link>
        </form>
      </Paper>
    </Box>
  );
}

export default LoginForm; 